import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as lo_ from 'lodash';
import * as _ from 'underscore';
import { RestService } from '../../services/rest/rest.service';
import { MetadataFiltersDataSource } from './metadata-filters-data-source';

@Component({
    standalone: false,
    selector: 'res-metadata-filters',
    templateUrl: './metadata-filters.component.html',
    styleUrls: ['./metadata-filters.component.scss',],
    encapsulation: ViewEncapsulation.None
})

export class MetadataFiltersComponent implements OnInit {
    @ViewChild('metadataFiltersForm', { static: true }) metadataFiltersForm: NgForm;

    get rows(): Array<any> {
        return this.dataSource.data;
    }
    @Input()
    set rows(val: Array<any>) {
        if (val && val.length > 0) {
            val.forEach(row => {
                this.setHasIconFlagForRow(row);
            });
            this.dataSource.set(val);
            this.resetState();
        }
    }
    dataSource = new MetadataFiltersDataSource<any>();
    currentFocusedIndex: number;
    errorIndices: Array<number>;

    @Input() automationId: string;
    @Input() validateName: boolean;
    @Input() isDisabled = false;
    @Input() isInFreeFormMode: boolean;
    @Input() metadataProperties: [];
    @Input() onDataSourceChanged: (data) => void;
    @Output() openPickerEmmiter = new EventEmitter();

    isClearedByBackspace = false;
    popoverErrors: Array<{
        error: boolean,
        popover: boolean,
        message: string
    }> = new Array<{
        error: boolean,
        popover: boolean,
        message: string
    }>();

    valid: boolean;
    defaultDatePlaceholders = ['Start YYYY-MM-DD ...', 'End YYYY-MM-DD ...'];
    minResult: number;
    maxResult: number;
    startDate: Date;
    endDate: Date;
    listButton: boolean;
    errorMessage: string;
    metadataWithValueSelector = ['docclass', 'docext', 'filetype', 'msgclass', 'msgsource', 'msoutlookmessageclass', 'origdocext', 'participantdomains', 'docannotations', 'hiddendata'];

    constructor(private element: ElementRef, private restService: RestService) {
    }

    ngOnInit(): void {
        this.valid = true;
    }

    onAddNewRow(index: number, row: any, last: boolean): void {
        if ((!_.isEmpty(row.selectedType)) && last) {
            this.addRow({
                query: '', selectedType: 'input', selectedValue: '', metadataProperties: lo_.cloneDeep(this.metadataProperties), allItems: lo_.cloneDeep(this.metadataProperties),
                minResult: '', maxResult: '', startDate: '', endDate: ''
            });
        }
    }

    onRemoveRow(index: number, row: any, last: boolean): void {
        if (this.rows[index]) {
            // need to recalculate error indices as indices may change after rows are deleted
            this.errorIndices = [];
            this.rows.forEach((rowInRows, index) => {
                if (rowInRows.hasError) {
                    this.errorIndices.push(index);
                }
            });
            this.removeRow(index);
        }
    }

    addRow(item: any, insertionIndex?: number): void {
        if (item) {
            if (this.popoverErrors.length !== this.rows.length) {
                this.popoverErrors.length = this.rows.length;
            }
            this.dataSource.push(item, insertionIndex);
            this.popoverErrors.push({ error: false, popover: false, message: null });
        }
    }

    removeRow(index: number): void {
        if (index >= 0 && index < this.rows.length) {
            if (this.popoverErrors.length !== this.rows.length) {
                this.popoverErrors.length = this.rows.length;
            }
            this.dataSource.splice(index, 1);
            this.popoverErrors.splice(index, 1);
            this.onModelChange(null);
        }
    }

    deleteAllRows(): void {
        this.rows = [{ query: '', selectedType: 'input', selectedValue: '', metadataProperties: lo_.cloneDeep(this.metadataProperties), allItems: lo_.cloneDeep(this.metadataProperties) }];
        this.onModelChange(null);
    }


    resetState(): void {
        this.metadataFiltersForm.form.markAsPristine();
        this.popoverErrors.length = this.rows.length;
        for (let index = 0; index < this.popoverErrors.length; index++) {
            this.popoverErrors[index] = {
                error: false,
                popover: false,
                message: null
            };
        }
    }

    onDropdownChange = (index: number, row: any, last: boolean, item) => {
        row.selectedValue = item.name;
        row.category = item.category;
        row.hasError = false;
        row.notValid = false;
        this.categoryChanged(row);
        this.setHasIconFlagForRow(row);
        if (row.selectedValue !== '') {
            this.onAddNewRow(index, row, last);
        }
        this.onModelChange(row);
    }

    filterData(row: any, index?: number, last?: boolean): void {
        row.notValid = false;
        row.hasError = false;
        row.errorMessage = '';
        if (row.selectedValue) {
            this.onAddNewRow(index, row, last);
            row.metadataProperties = row.allItems.filter(value => {
                return value?.name.toLowerCase()?.trim().includes(row.selectedValue?.toLowerCase()?.trim());
            });
        }
        else {
            row.metadataProperties = row.allItems;
            row.selectedType = 'input';
        }
        row.metadataProperties.forEach((entry) => {
            if (entry.name.toLowerCase()?.trim() === row.selectedValue.toLowerCase()?.trim()) {
                row.name = entry.name;
                row.category = entry.category;
            }
        });
    }

    validateOnBlur(row, index: number, last: boolean): void {
        let validFields = 0;
        if (row?.metadataProperties) {
            row.metadataProperties.forEach((entry) => {
                if (entry.name.toLowerCase()?.trim() === row.selectedValue.toLowerCase()?.trim()) {
                    validFields = validFields + 1;
                    row.category = entry.category;
                }
            });
        }
        if (validFields === 0 && !_.isEmpty(row.selectedValue)) {
            row.notValid = true;
            row.hasError = true;
            row.errorMessage = 'Please enter a valid metadata field name';
        }
        else {
            row.notValid = false;
            row.hasError = false;
            row.errorMessage = '';
            this.categoryChanged(row);
            if (row.selectedValue !== '') {
                setTimeout(() => {
                    this.onAddNewRow(index, row, last);
                }, 300);
            }
            this.onModelChange(row);
        }
    }

    categoryChanged(row): void {
        if (row.selectedValue) {
            if (row?.category === 'INTEGER' || row?.category === 'LONGINTEGER') {
                row.selectedType = 'range';
            } else if (row?.category === 'AURARIADATE' || row?.category === 'DATE') {
                row.selectedType = 'date';
            } else {
                row.selectedType = 'input';
            }
            this.clearRowData(row);
        }
    }

    clearRowData(row): void {
        if (row) {
            switch (row.selectedType) {
                case 'range': {
                    row.query = '';
                    row.startDate = '';
                    row.endDate = '';
                    break;
                }
                case 'date': {
                    row.query = '';
                    row.minResult = '';
                    row.maxResult = '';
                    break;
                }
                case 'input': {
                    row.startDate = '';
                    row.endDate = '';
                    row.minResult = '';
                    row.maxResult = '';
                    break;
                }
            }
        }
    }

    getAllItems(event, row): void {
        if (row.metadataProperties !== row.allItems) {
            const rowIdx = this.rows.indexOf(row);
            this.hideDropdown(event, rowIdx);
            this.scrollToSelectedItem(rowIdx);
        }
        if (row.allItems) {
            this.listButton = true;
            row.metadataProperties = row.allItems;
        }
    }

    scrollToSelectedItem(rowIdx: number): void {
        setTimeout(() => {
            const dropdownMenuControl = document.getElementById(this.automationId + rowIdx);
            if (dropdownMenuControl) {
                const selectedLi = Array.from(dropdownMenuControl.children).find(li => li.className === 'custom-dropdown-option selected-option');
                if (selectedLi) {
                    selectedLi.scrollIntoView({ block: 'nearest' });
                }
            }
        });
    }

    hideDropdown(event?: Event, rowIdx?: number): void {
        const dropdownMenuControl = document.getElementById(this.automationId + rowIdx);
        if (dropdownMenuControl?.className === 'dropdown-menu show') {
            if (event) {
                event.stopPropagation();
            } else {
                dropdownMenuControl.classList.remove('show');
            }
        }
    }

    tabOut = (event: any, button?: string, row?: any) => {
        if (event?.key === 'Tab' || (event && button === 'listAllButton')) {
            if (this.listButton && button) {
                event.stopPropagation();
                this.listButton = false;
            }

            if (event?.currentTarget?.previousSibling !== event?.relatedTarget) {
                const rowIdx = this.rows.indexOf(row);
                if (this.isDropdownOpen(rowIdx) && event?.key !== 'Tab') {
                    this.hideDropdown();
                } else {
                    setTimeout(() => {
                        if (event?.target?.nodeName === 'BUTTON') {
                            this.getAllItems(event, row);
                        } else {
                            this.filterData(row);
                        }

                    });
                }
            }
        }
    }

    isDropdownOpen(rowIdx: number): boolean {
        const dropdown = document.getElementById(this.automationId + rowIdx);
        return dropdown && dropdown.className === 'dropdown-menu show';
    }

    onClick(event, row, index, last): void {
        const rowIdx = this.rows.indexOf(row);
        this.filterData(row);
        this.hideDropdown(event, rowIdx);

        if (!this.isDropdownOpen(rowIdx)) {
            this.hideDropdown();
        }

        this.validateOnBlur(row, index, last);
    }

    trackByFn(index: number) {
        return index;
    }

    onModelChange(row): void {
        if (this.onDataSourceChanged && !row?.hasError) {
            this.setHasIconFlagForRow(row);
            this.onDataSourceChanged(row);
        }
    }

    setHasIconFlagForRow(row): void {
        if (row?.selectedValue) {
            row.hasIcon = this.metadataWithValueSelector.indexOf(row.selectedValue) > -1;
        }
    }

    openPicker(row): void {
        if (this.openPickerEmmiter) {
            this.openPickerEmmiter.emit(row);
        }
    }

}